import { getFileExtension } from "@@/utilities/get-file-extension";
import { PublicError } from "../errors/public-error";

const defaultContentType = "application/octet-stream";

// Images
const imageContentTypes = [
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/webp",
    "image/gif",
    "image/heic",
    "image/heif",
    "image/svg+xml",
] as const;
const imageContentTypesWithoutHeicHeif = imageContentTypes.filter(
    item =>
        // ignore heif and heic
        // since they are only supported
        // in safari and ios
        item !== "image/heif" && item !== "image/heic",
);
type StorageItemImageContentType = (typeof imageContentTypes)[number];

const imageFileTypes = imageContentTypes.map(item =>
    item.replace("image/", "").replace("+xml", ""),
);
const imageFileTypesWithoutHeicHeif = imageFileTypes.filter(fileType => {
    // ignore heif and heic since they not that widely supported
    return fileType !== "heif" && fileType !== "heic";
});

const isImageFileType = (filename: string): boolean => {
    const extension = getFileExtension(filename).toLowerCase();
    return imageFileTypes.includes(extension);
};

const isImageContentType = (
    contentType: string,
): contentType is StorageItemImageContentType => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return imageContentTypes.includes(contentType as any);
};

const guessImageContentTypeFromFilename = (
    filename: string,
): StorageItemImageContentType => {
    const extension = getFileExtension(filename.trim()).toLowerCase();
    const imageContentType = `image/${
        extension === "svg" ? "svg+xml" : extension
    }` as StorageItemContentType;
    if (!isImageContentType(imageContentType)) {
        throw new PublicError(
            `Felaktig filtyp, endast följande filtyper accepteras: ${imageFileTypes.join(
                ", ",
            )}`,
        );
    }
    return imageContentType;
};

// Pdf
const pdfContentType = "application/pdf";
type StorageItemPdfContentType = typeof pdfContentType;
const pdfFileType = "pdf";

const isPdfFiletype = (filename: string): boolean => {
    const extension = getFileExtension(filename).toLowerCase();
    return pdfFileType === extension;
};
const isPdfContentType = (
    contentType: string,
): contentType is StorageItemPdfContentType => {
    return contentType === pdfContentType;
};

// Svg
const svgContentType = "image/svg+xml";
type StorageItemSvgContentType = typeof svgContentType;
const svgFileType = "svg";

const isSvgFiletype = (filename: string): boolean => {
    const extension = getFileExtension(filename).toLowerCase();
    return svgFileType === extension;
};
const isSvgContentType = (
    contentType: string,
): contentType is StorageItemSvgContentType => {
    return contentType === svgContentType;
};

// Generic
const storageItemContentTypes = [
    defaultContentType,
    pdfContentType,
    ...imageContentTypes,
] as const;
type StorageItemContentType =
    | StorageItemImageContentType
    | StorageItemPdfContentType
    | typeof defaultContentType;

const guessContentTypeFromFilename = (
    filename: string,
): StorageItemContentType => {
    if (isImageFileType(filename))
        return guessImageContentTypeFromFilename(filename);
    if (isPdfFiletype(filename)) return pdfContentType;
    return defaultContentType;
};

export {
    guessContentTypeFromFilename,
    guessImageContentTypeFromFilename,
    // images
    imageContentTypes,
    imageContentTypesWithoutHeicHeif,
    imageFileTypes,
    imageFileTypesWithoutHeicHeif,
    isImageContentType,
    isPdfContentType,
    isPdfFiletype,
    isSvgContentType,
    isSvgFiletype,
    // pdf
    pdfContentType,
    pdfFileType,
    storageItemContentTypes,
    // svg
    svgContentType,
    svgFileType,
};
export type {
    StorageItemContentType,
    StorageItemImageContentType,
    StorageItemPdfContentType,
    StorageItemSvgContentType,
};
